import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Card, Stack, Typography } from '@mui/material';
import useUnitProvider from '/@/core/context/unit';
import type { LastCampaign } from '/@/hooks/crop-monitoring/use-last-campaign';
import { _ } from '/@/locale';
import { formatDate } from '/@/utils';

/**
 * "Contexto" panel of the design: what the field actually yielded last campaign.
 *
 * `compact` is the phone layout — the card fills the width, the icon sits on its own line
 * and the yield leads, with the metric name underneath. On desktop it's a fixed-width card
 * in the right-hand column with the name above a display-sized figure.
 */
function LastCampaignCard({
    fieldName,
    lastCampaign,
    compact,
}: {
    fieldName?: string;
    lastCampaign: LastCampaign;
    compact?: boolean;
}) {
    const units = useUnitProvider();
    const cornYield = units.toCornYield(lastCampaign.averageYield, 0);

    const fieldNameLine = fieldName && (
        <Typography variant='overline' color='primary.mui.inactive' textTransform='uppercase'>
            {fieldName}
        </Typography>
    );
    const details = (
        <Stack>
            {lastCampaign.plantingDate && (
                <Typography variant={compact ? 'caption' : 'body2'} color='primary'>
                    {_('Fecha de siembra: {{date}}', {
                        date: formatDate(new Date(lastCampaign.plantingDate)),
                    })}
                </Typography>
            )}
            {lastCampaign.productName && (
                <Typography variant={compact ? 'caption' : 'body2'} color='primary'>
                    {_('Híbrido sembrado: {{product}}', { product: lastCampaign.productName })}
                </Typography>
            )}
        </Stack>
    );

    return (
        <Stack spacing={2} width={compact ? '100%' : undefined}>
            {!compact && (
                <Typography variant='overline' color='primary.mui.inactive' textTransform='uppercase'>
                    {_('Contexto')}
                </Typography>
            )}
            <Card
                elevation={0}
                sx={{
                    background: (theme) => theme.palette.primary.infoBackground,
                    borderRadius: '8px',
                    p: 2,
                }}
            >
                {compact ? (
                    <Stack spacing={1}>
                        <Stack>
                            <TrendingUpIcon color='primary' />
                            {fieldNameLine}
                            <Typography variant='subtitle1' fontWeight={700} color='primary'>
                                {cornYield.localeValue} {cornYield.unit}
                            </Typography>
                            <Typography variant='subtitle1' color='primary'>
                                {_('Rendimiento última campaña')}
                            </Typography>
                        </Stack>
                        {details}
                    </Stack>
                ) : (
                    <Stack spacing={1} width={262}>
                        {fieldNameLine}
                        <Stack direction='row' spacing={1} alignItems='flex-start'>
                            <TrendingUpIcon color='primary' />
                            <Typography variant='subtitle1' fontWeight={700} color='primary'>
                                {_('Rendimiento última campaña')}
                            </Typography>
                        </Stack>
                        <Typography variant='h4' color='primary'>
                            {cornYield.localeValue}{' '}
                            <Typography variant='h6' component='span' color='primary'>
                                {cornYield.unit}
                            </Typography>
                        </Typography>
                        {details}
                    </Stack>
                )}
            </Card>
        </Stack>
    );
}

export default LastCampaignCard;
